import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Waves, Sun, Heart, Coffee, Sparkles, Calendar, MapPin, Clock, CheckCircle2, Users, X, User, Mail, Phone, Lock, Info } from 'lucide-react';
import { openTranzilaPayment } from '../utils/tranzila';
import { sendWomensSupRegistrationToMake } from '../utils/makeWebhook';
import { UtmCapture } from '../components/UtmCapture';
import { getUtmParams } from '../utils/utmTracking';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Open registration dates — keep in sync with the hero / details sections
const EVENT_DATES = [{
  value: '04.09.2026',
  label: 'שישי, 04.09.2026'
}];
export function WomensSUPMorningPage() {
  const PRICE = 480;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // ============================================
  // TEST MODE - DELETE THIS BLOCK TO REMOVE
  // Activate via URL: ?test_mode=womenssup2026
  // ============================================
  const [isTestMode, setIsTestMode] = useState(false);
  // ============================================
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    // Only one open date right now, so it starts selected
    selectedDate: EVENT_DATES[0].value,
    dateOfBirth: '',
    noMedicalLimitations: false,
    hasExperience: '',
    notes: ''
  });
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);
  // Detect payment success — runs in both top window AND iframe context.
  // If we're inside an iframe at /womens-sup-morning?paid=1, send the
  // registration to Make.com (ONLY happens after a successful Tranzila payment)
  // and then notify the parent window so it can show the success state.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Detect success from EITHER our own marker OR Tranzila's response param.
    // Tranzila replaces our query string with its own params on redirect.
    const isPaid = urlParams.get('paid') === '1' || urlParams.get('Response') === '000';
    if (!isPaid) return;
    console.log('💳 Payment success detected on page load', {
      paid: urlParams.get('paid'),
      Response: urlParams.get('Response'),
      ConfirmationCode: urlParams.get('ConfirmationCode'),
      isInIframe: window.parent !== window
    });
    const sendWebhookAndNotify = async () => {
      // Read the registration data saved at form submission
      let registration: any = null;
      try {
        const stored = localStorage.getItem('womensSupMorningRegistration');
        if (stored) registration = JSON.parse(stored);
      } catch (err) {
        console.warn('Could not read registration from localStorage', err);
      }
      // Send to Make.com — only after a confirmed successful payment
      if (registration) {
        // Prefer UTM params stored with the registration (captured at submit
        // time, more reliable in Meta in-app browsers). Fall back to the
        // global UTM store if anything is missing.
        const fallbackUtm = getUtmParams();
        const utm = {
          utm_source: registration.utm_source || fallbackUtm.utm_source,
          utm_medium: registration.utm_medium || fallbackUtm.utm_medium,
          utm_campaign: registration.utm_campaign || fallbackUtm.utm_campaign,
          utm_content: registration.utm_content || fallbackUtm.utm_content,
          utm_term: registration.utm_term || fallbackUtm.utm_term,
          utm_id: registration.utm_id || fallbackUtm.utm_id,
          fbclid: registration.fbclid || fallbackUtm.fbclid
        };
        try {
          await sendWomensSupRegistrationToMake({
            type: 'Womens SUP Morning Registration',
            source: 'MagicPatterns',
            paid: true,
            event: registration.event || 'בוקר נשי - הכוח שבמים',
            eventDate: registration.eventDate || '04.09.2026',
            selectedDate: registration.selectedDate || registration.eventDate || '',
            fullName: registration.fullName || '',
            email: registration.email || '',
            phone: registration.phone || '',
            dateOfBirth: registration.dateOfBirth || '',
            noMedicalLimitations: !!registration.noMedicalLimitations,
            hasExperience: registration.hasExperience || '',
            notes: registration.notes || '',
            price: registration.price ?? '',
            submittedAt: registration.submittedAt || new Date().toISOString(),
            ...utm
          });
          // NOTE: wm_purchase pixel event is fired in the TOP WINDOW
          // (see message listener below), NOT here in the iframe — the
          // iframe is too short-lived for fbevents.js to finish loading
          // and dispatch the network call before the modal closes.
          // Clear stored data after a successful send
          localStorage.removeItem('womensSupMorningRegistration');
        } catch (err) {
          console.error('Webhook send failed', err);
        }
      }
      if (window.parent !== window) {
        // Inside the Tranzila iframe — tell the parent to show the success UI.
        // Include submittedAt so the parent can dedup wm_purchase even if it
        // already cleared localStorage.
        try {
          window.parent.postMessage({
            type: 'womens-sup-payment-success',
            submittedAt: registration?.submittedAt
          }, window.location.origin);
        } catch (err) {
          console.warn('Could not postMessage to parent', err);
        }
      } else {
        // Top-level visit with paid=1 (fallback). Show success directly.
        setPaymentSuccess(true);
        setIsPaymentOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    sendWebhookAndNotify();
  }, []);
  // Top window: listen for the success message from the iframe.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'womens-sup-payment-success') {
        setPaymentSuccess(true);
        setIsSubmitting(false);
        // Meta Pixel — fire wm_purchase custom event HERE in the top window.
        // The top window has had the full app loaded for the entire session,
        // so fbevents.js is fully initialized and the call will dispatch
        // a proper /tr/ network request with all parameters.
        // Dedup on submittedAt to avoid double-firing on remounts.
        try {
          const stored = localStorage.getItem('womensSupMorningRegistration');
          const reg = stored ? JSON.parse(stored) : null;
          const submittedAt = reg?.submittedAt || event.data?.submittedAt || '';
          const dedupKey = `wm_purchase_fired_${submittedAt}`;
          const alreadyFired = submittedAt && localStorage.getItem(dedupKey);
          if (!alreadyFired && typeof window !== 'undefined' && (window as any).fbq) {
            ;
            (window as any).fbq('trackCustom', 'wm_purchase', {
              content_name: 'בוקר נשי - הכוח שבמים',
              content_category: 'Womens SUP Morning',
              value: reg?.price ?? PRICE,
              currency: 'ILS',
              utm_source: reg?.utm_source,
              utm_medium: reg?.utm_medium,
              utm_campaign: reg?.utm_campaign,
              utm_content: reg?.utm_content,
              utm_term: reg?.utm_term,
              utm_id: reg?.utm_id,
              fbclid: reg?.fbclid
            });
            if (submittedAt) localStorage.setItem(dedupKey, '1');
          }
        } catch (err) {
          console.warn('Could not fire wm_purchase pixel event', err);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  // ============================================
  // TEST MODE - DELETE THIS BLOCK TO REMOVE
  // ============================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testModeParam = urlParams.get('test_mode');
    if (testModeParam === 'womenssup2026') {
      setIsTestMode(true);
      localStorage.setItem('womens_test_mode_active', 'true');
      console.log('🧪 WOMENS SUP TEST MODE ACTIVATED - Charge will be ₪1');
    } else {
      const stored = localStorage.getItem('womens_test_mode_active');
      if (stored === 'true') {
        setIsTestMode(true);
        console.log('🧪 WOMENS SUP TEST MODE ACTIVE (from localStorage)');
      }
    }
  }, []);
  // ============================================
  const openForm = () => {
    setPaymentSuccess(false);
    setIsFormOpen(true);
    // Meta Pixel — track form opened (AddToCart equivalent)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'בוקר נשי - הכוח שבמים',
        content_category: 'Womens SUP Morning',
        value: PRICE,
        currency: 'ILS'
      });
    }
  };
  const closeForm = () => {
    if (!isSubmitting) setIsFormOpen(false);
  };
  const closePayment = () => {
    setIsPaymentOpen(false);
    setIsSubmitting(false);
    setPaymentSuccess(false);
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.selectedDate || !formData.dateOfBirth || !formData.noMedicalLimitations || !formData.hasExperience) return;
    setIsSubmitting(true);
    // ============================================
    // TEST MODE - DELETE THIS BLOCK TO REMOVE
    // ============================================
    const chargeSum = isTestMode ? 1 : PRICE;
    // ============================================
    // Capture UTM params NOW at form submission time so they survive any
    // localStorage clearing that might happen in Meta in-app browsers.
    const utmParams = getUtmParams();
    console.log('📊 Capturing UTM params at form submission:', utmParams);
    // Persist registration for later record keeping / thank you page
    try {
      localStorage.setItem('womensSupMorningRegistration', JSON.stringify({
        ...formData,
        event: 'בוקר נשי - הכוח שבמים',
        eventDate: formData.selectedDate,
        price: chargeSum,
        submittedAt: new Date().toISOString(),
        // UTM params stored alongside registration
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        utm_id: utmParams.utm_id,
        fbclid: utmParams.fbclid
      }));
    } catch (err) {
      console.warn('Could not persist registration to localStorage', err);
    }
    // Track Meta Pixel event if available
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'בוקר נשי - הכוח שבמים',
        content_category: 'Womens SUP Morning',
        value: chargeSum,
        currency: 'ILS'
      });
    }
    // Close form modal and open the payment modal with embedded iframe
    setIsFormOpen(false);
    setIsPaymentOpen(true);
    // Defer to ensure the iframe (name="tranzila") is in the DOM before submitting the form
    setTimeout(() => {
      openTranzilaPayment({
        sum: chargeSum,
        contact: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        pdesc: `בוקר נשי - הכוח שבמים (${formData.selectedDate})`,
        success_url: `${window.location.origin}/womens-sup-morning?paid=1`,
        fail_url: window.location.href
      });
    }, 150);
  };
  // If we're loaded inside the Tranzila iframe at /womens-sup-morning?paid=1,
  // skip rendering the full landing page. The iframe is short-lived and only
  // needs to send the webhook + postMessage to the parent. Showing the full
  // page would briefly flash all the marketing content inside the modal.
  const isPaidIframe = typeof window !== 'undefined' && window.parent !== window && (new URLSearchParams(window.location.search).get('paid') === '1' || new URLSearchParams(window.location.search).get('Response') === '000');
  if (isPaidIframe) {
    return <div dir="rtl" className="font-['Heebo'] min-h-screen bg-rose-50 flex items-center justify-center text-stone-700" data-id="element-2543" data-mp="1m9dtc4atnray">
        <div className="text-center" data-id="element-2544" data-mp="1hcdn9tdz2oju">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-rose-200 border-t-rose-500 animate-spin" data-id="element-2545" data-mp="1va266vohblm9" />
          <p className="text-base font-medium" data-id="element-2546" data-mp="1r7fcsqc5tsmy">מאשרת את ההרשמה…</p>
        </div>
      </div>;
  }
  return <div dir="rtl" className="font-['Heebo'] bg-rose-50 text-stone-800 min-h-screen overflow-x-hidden" data-id="element-2547" data-mp="256op3ex8w2ln">
      {/* Silent UTM capture for Meta Ads attribution */}
      <UtmCapture data-id="element-2548" data-mp="2eyaxz4m41pl8" />

      {/* ============================================ */}
      {/* TEST MODE INDICATOR - DELETE THIS TO REMOVE */}
      {/* ============================================ */}
      {isTestMode && <div className="fixed top-4 right-4 z-[80] bg-yellow-400 text-stone-900 px-4 py-2 rounded-lg shadow-lg font-bold text-sm flex items-center gap-2" data-id="element-2549" data-mp="2nxpp86a3q46s">
          <Sparkles className="w-4 h-4" data-id="element-2550" data-mp="2ixjqy79btjfq" />
          TEST MODE - ₪1
        </div>}
      {/* ============================================ */}

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-28 md:py-24" data-id="element-2551" data-mp="m80icgsmba0y">
        <div className="absolute inset-0 z-0" data-id="element-2552" data-mp="r0ton93qiqf5">
          <img src="https://cdn.magicpatterns.com/uploads/7UQXFa8QVzGYPRJjgEmh4j/c61b37f1-fb9e-4cd8-b43e-2e77a03ab830_(1).jpg" alt="בוקר נשי על הסאפ בכנרת" className="w-full h-full object-cover" data-id="element-2553" data-mp="v2578uo6ybdu" />
          <div className="absolute inset-0 bg-gradient-to-b from-rose-900/30 via-rose-800/40 to-stone-900/70" data-id="element-2554" data-mp="39xpts0v2uye"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-2555" data-mp="7b9k4auok4cx">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-2556" data-mp="cbe0m0vl8z35">
            {/* Sold-out dates — August now sold out too */}
            <div className="inline-flex flex-col items-center justify-center gap-3 py-4 px-6 sm:px-8 rounded-3xl bg-rose-500/30 text-white border-2 border-rose-300/60 mb-4 backdrop-blur-md shadow-2xl shadow-rose-900/40" data-id="element-2557" data-mp="1ns3xuui1agkm">
              <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-rose-200/80 font-semibold" data-id="element-2558" data-mp="1o3wjn0vx47te">
                המפגשים הקרובים אזלו
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-2559" data-mp="1k108fv8pjdzh">
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-stone-900/40 text-stone-200 border border-stone-500/40 text-base sm:text-lg font-semibold backdrop-blur-sm relative overflow-hidden" data-id="element-2560" data-mp="1f1n9ic4kaggj">
                  <Calendar className="w-4 h-4" data-id="element-2561" data-mp="1148fsv79zmir" />
                  <span className="line-through opacity-70" data-id="element-2562" data-mp="115jjkv0q0u9v">03.07.2026</span>
                  <span className="absolute inset-0 flex items-center justify-center bg-stone-900/60 font-bold text-rose-400 tracking-wider text-xs uppercase" style={{
                  transform: 'rotate(-5deg)'
                }} data-id="element-2563" data-mp="2zijv5mo8uiby">
                    SOLD OUT
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-stone-900/40 text-stone-200 border border-stone-500/40 text-base sm:text-lg font-semibold backdrop-blur-sm relative overflow-hidden" data-id="element-2564" data-mp="xmt5j5tbgjrr">
                  <Calendar className="w-4 h-4" data-id="element-2565" data-mp="nu594c690htk" />
                  <span className="line-through opacity-70" data-id="element-2566" data-mp="1blkfcg15bja3">07.08.2026</span>
                  <span className="absolute inset-0 flex items-center justify-center bg-stone-900/60 font-bold text-rose-400 tracking-wider text-xs uppercase" style={{
                  transform: 'rotate(-5deg)'
                }} data-id="element-2567" data-mp="1gkna97new94c">
                    SOLD OUT
                  </span>
                </span>
              </div>
            </div>

            {/* Remaining spots — only one open date left */}
            <div className="inline-flex flex-col items-center justify-center gap-2 py-4 px-6 sm:px-8 rounded-3xl bg-emerald-500/25 text-white border-2 border-emerald-300/60 mb-6 backdrop-blur-md shadow-2xl shadow-emerald-900/30" data-id="element-2568" data-mp="3sa6blysf2uxv">
              <span className="inline-flex items-center gap-2 text-lg sm:text-2xl font-extrabold" data-id="element-2569" data-mp="3fr2da7jp4gqe">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" data-id="element-2570" data-mp="3tqagrcmw6b0e" />
                נותרו מקומות בודדים לבוקר נשי ב-4.9.2026
              </span>
              <span className="text-sm sm:text-base text-emerald-50/90 font-medium" data-id="element-2571" data-mp="312xs8wx67t4g">
                תאריך באוקטובר יפורסם בקרוב
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-4 leading-tight" data-id="element-2572" data-mp="2w5fqprlhgg6h">
              בוקר נשי - <span className="text-rose-300" data-id="element-2573" data-mp="3au28dzyl1k0v">הכוח שבמים</span>
            </h1>
            <p dir="ltr" className="text-2xl md:text-4xl text-rose-100 mb-6 font-light italic tracking-wide" data-id="element-2574" data-mp="36194feaxufpu">
              Come Sup With Me
            </p>
            <p className="text-xl md:text-2xl text-rose-50 mb-10 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-2575" data-mp="2ddmy3yabswza">
              בואי ככה, בדיוק כמו שאת. כל השאר – עליי.
            </p>
            <button type="button" onClick={openForm} className="group inline-flex items-center justify-center gap-2.5 bg-rose-500 hover:bg-rose-600 text-white text-base sm:text-lg font-bold py-4 px-8 sm:px-10 rounded-full transition-all duration-200 shadow-xl shadow-rose-900/40 hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300/60" data-id="element-2576" data-mp="1rwenjtxaygza">
              <Sparkles className="w-5 h-5 shrink-0" data-id="element-2577" data-mp="2frpvibitolze" />
              <span data-id="element-2578" data-mp="2fq6cwe9p1yug">הרשמה ותשלום לתאריכים הבאים</span>
              <span className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-2579" data-mp="2ki68xvmn87lr">
                ←
              </span>
            </button>
          </motion.div>
        </div>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 z-10" data-id="element-2580" data-mp="2uh5jjjqyydbe">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1" data-id="element-2581" data-mp="mvn9hh40danz">
            <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-2582" data-mp="it0mpum72fxn"></div>
          </div>
        </motion.div>
      </section>

      {/* Invitation / Personal Note */}
      <section className="py-24 px-4 bg-white" data-id="element-2583" data-mp="dwjq0c0xit8y">
        <div className="max-w-3xl mx-auto text-center" data-id="element-2584" data-mp="8w5shnwyf1p6">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-2585" data-mp="8xp8reim1cqy">
            <div className="inline-flex items-center gap-2 mb-6 text-rose-500" data-id="element-2586" data-mp="11lcnicx7995o">
              <Heart className="w-5 h-5" data-id="element-2587" data-mp="wsjkbthdq8vp" />
              <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-2588" data-mp="wrgw5tbfykjh">
                ההזמנה שלי אלייך
              </span>
              <Heart className="w-5 h-5" data-id="element-2589" data-mp="1p9cdvbqlmxjq" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-8 leading-tight" data-id="element-2590" data-mp="1pax7mz53acgj">
              לפעמים את צריכה רק בוקר אחד.
            </h2>
            <div className="space-y-5 text-lg md:text-xl text-stone-600 leading-relaxed" data-id="element-2591" data-mp="226nq168gk6cr">
              <p data-id="element-2592" data-mp="1x6i323d4v810">
                בוקר אחד שבו את מתנתקת מהיומיום, מתמסרת, מתחברת אל הטבע, ומרגישה
                את הכוח חוזר אלייך.
              </p>
              <p className="font-medium text-stone-800" data-id="element-2593" data-mp="1s885jm3xrvc8">
                בלי ניסיון קודם. בלי שיפוטיות. רק את, המים וקבוצת נשים בדיוק
                כמוך.
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="mt-14 rounded-3xl overflow-hidden shadow-xl shadow-rose-200/40 border border-rose-100" data-id="element-2594" data-mp="2kw430t1bxra2">
              <img src="https://cdn.magicpatterns.com/uploads/pvDjVs7yuSgf7363qaT2WF/IMG_8179_(1).jpg" alt="קבוצת נשים בבוקר נשי בחוף מעגן" className="w-full h-auto object-cover" data-id="element-2595" data-mp="33nmzsi4rg93n" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Adi */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-rose-50" data-id="element-2596" data-mp="qctxrqvwyxo0">
        <div className="max-w-3xl mx-auto" data-id="element-2597" data-mp="qey5kjwxogtk">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="bg-white rounded-3xl shadow-xl shadow-rose-100/50 border border-rose-100 p-8 md:p-12" data-id="element-2598" data-mp="ue5sgatna932">
            <div className="inline-flex items-center gap-2 mb-5 text-rose-500" data-id="element-2599" data-mp="14aijf0xt28u4">
              <Heart className="w-4 h-4" data-id="element-2600" data-mp="199v87r88lpzc" />
              <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2601" data-mp="1e2oeijjct6dj">
                מי שמחכה לך שם
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight" data-id="element-2602" data-mp="1i3i21bsvwj5w">
              שלום, אני עדי
            </h2>

            <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed" data-id="element-2603" data-mp="1s27akdyw4r9h">
              <p data-id="element-2604" data-mp="3o5byhjmskper">
                גיליתי את עולם הסאפ בשנים האחרונות וזה שינה אותי מבחוץ ומבפנים.
                התמכרתי - ואני מזמינה גם אותך לחולל בעצמך שינוי אמיתי, כזה שיעיר
                ויעורר, ירגש ויסחוף.
              </p>
              <p data-id="element-2605" data-mp="1q0z161uiuw9">
                ההתמודדות עם הטבע, השקט והתנועה המתמדת על המים אפשרה לי להתחבר
                לקול הפנימי שלי ולגלות בי עוצמות שלא הכרתי.
              </p>
              <p className="font-semibold text-stone-800 text-lg md:text-xl pt-2" data-id="element-2606" data-mp="aq5wandbscwp">
                מזמינה גם אותך לגלות בעצמך עולמות חדשים. 🌊
              </p>
            </div>

            <div className="mt-8 mb-2 flex items-center gap-3" data-id="element-2607" data-mp="6r6u3aizau8y">
              <div className="h-px bg-rose-200 flex-1" data-id="element-2608" data-mp="knanj1m12ykd"></div>
              <span className="text-rose-400 text-2xl" style={{
              fontFamily: '"Brush Script MT", cursive'
            }} data-id="element-2609" data-mp="fpsbv3gmm58o">
                Adi
              </span>
              <div className="h-px bg-rose-200 flex-1" data-id="element-2610" data-mp="2mhtbop9y1e48"></div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="mt-10 rounded-2xl overflow-hidden shadow-lg shadow-rose-200/40 border border-rose-100" data-id="element-2611" data-mp="2hiq6n4tyr7w9">
              <img src="https://cdn.magicpatterns.com/uploads/o8NBipbPs9TUtEVJiiwzvh/image.png" alt="עדי על קרש סאפ בכנרת" className="w-full h-auto object-cover" data-id="element-2612" data-mp="s7csczo1m0c9" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What's in the morning */}
      <section className="py-24 px-4 bg-gradient-to-b from-rose-50 to-orange-50" data-id="element-2613" data-mp="4affv5ml1qo0">
        <div className="max-w-6xl mx-auto" data-id="element-2614" data-mp="4cjvgktuge44">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-2615" data-mp="9abm7bir5u6q">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2616" data-mp="23nt9cxf8wp18">
              מה מחכה לך בבוקר הזה?
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full" data-id="element-2617" data-mp="3kie6chkyjmu"></div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2618" data-mp="3vpcgw7relqrk">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/hfUVkEZQekPyb43CZMktV3/GetAttachmentThumbnail_(7).jpg',
            title: 'מעגל פתיחה',
            desc: 'כוס קפה חם, היכרות עדינה והכוונה לבוקר שלנו.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/iZHHfKbDuzWiZmbvcXG53T/IMG_8165.jpg',
            title: 'סאפ בכנרת',
            desc: 'ממש מאפס - היכרות עם מבנה הסאפ והמשוט, טכניקת חתירה, וחתירה במים.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/sFwj9YB7rvdyCPXtDrtWRm/IMG_5724.jpg',
            title: 'בראנץ׳ נשי',
            desc: 'ארוחת בוקר מפנקת, יין טוב, שיחה אמיתית וסגירת מעגל.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/eom8E644yCYiU5JAxE9kj9/IMG_5754.jpg',
            title: 'נשימות ואמבטיית קרח',
            desc: 'הדרכת נשימות מעמיקה וטבילה מעצימה במי קרח מול הנוף.'
          }].map((item: any, idx) => {
            const Icon = item.icon;
            return <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 rounded-3xl text-center hover:shadow-xl transition-shadow border border-rose-100 flex flex-col items-center" data-id="element-2619" data-mp="2xujykj1g754k" data-mpi={idx}>
                  {item.image ? <div className="w-20 h-20 rounded-full overflow-hidden mb-5 ring-4 ring-rose-100 shadow-md" data-id="element-2620" data-mp="254jrcdjh5fyj">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-2621" data-mp="2a3wb2pf1u6ww" />
                    </div> : <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mb-5" data-id="element-2622" data-mp="1hh37lza9ytj7">
                      <Icon className="w-8 h-8 text-rose-500" data-id="element-2623" data-mp="1mfwp02son4on" />
                    </div>}
                  <h3 className="text-xl font-bold mb-3 text-stone-800" data-id="element-2624" data-mp="1efgvzk55aq01">
                    {item.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-2625" data-mp="19h5m59euofht">{item.desc}</p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Ice Bath & Breathing Section */}
      <section className="py-24 px-4 bg-white overflow-hidden" data-id="element-2626" data-mp="1j78gpe1me1i9">
        <div className="max-w-6xl mx-auto" data-id="element-2627" data-mp="vgcoift954ah">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2628" data-mp="vh4m1w6etc3e">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} data-id="element-2629" data-mp="qib6zzbh5d14">
              <div className="inline-flex items-center gap-2 mb-4 text-rose-500" data-id="element-2630" data-mp="10i21i101bs17">
                <Sparkles className="w-5 h-5" data-id="element-2631" data-mp="2gs4gqlwnmi2g" />
                <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-2632" data-mp="2bvdzvwd0zifz">
                  העצמה דרך הגוף
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight" data-id="element-2633" data-mp="2jggj1drfyhh9">
                הכוח שבנשימה ובקור
              </h2>

              <div className="space-y-6 text-lg text-stone-600 leading-relaxed" data-id="element-2634" data-mp="2aeh0ui4ox7cq">
                <p data-id="element-2635" data-mp="2eer3plx6rhnd">
                  <strong className="text-stone-800" data-id="element-2636" data-mp="2egbzfev8l6mt">
                    חשיבות מערכת הנשימה:
                  </strong>{' '}
                  הנשימה היא העוגן שלנו. הדרכת הנשימות שנחווה יחד תלמד אותנו איך
                  להשתמש בנשימה כדי לווסת את מערכת העצבים, להרגיע את הגוף
                  ולהחזיר את השליטה לידיים שלנו.
                </p>
                <p data-id="element-2637" data-mp="2taqkdkyd2q44">
                  <strong className="text-stone-800" data-id="element-2638" data-mp="2xdwqmslh4hal">טבילה במי קרח:</strong>{' '}
                  מעבר ליתרונות הפיזיים המוכחים כמו חיזוק המערכת החיסונית, הפחתת
                  דלקתיות ושיפור זרימת הדם — הטבילה בקרח היא אימון מנטלי טהור.
                  היא מפגישה אותנו עם קושי קיצוני ומלמדת אותנו שאנחנו מסוגלות
                  להכיל אותו.
                </p>
                <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100 mt-4" data-id="element-2639" data-mp="315wd2dgbulgd">
                  <p className="text-stone-800 font-medium italic" data-id="element-2640" data-mp="2wbs4jprey5d6">
                    "ההתמודדות עם החתירה בכנרת והיכולת לנשום בתוך הקור, לצאת
                    ממצב של סטרס ולהרפות אל תוך הקושי — היא בדיוק אותה היכולת
                    שתשרת אותנו מול אתגרי היום-יום. כשאנחנו מתמודדות עם אתגר חדש
                    ומנצחות את הקרח - אנחנו מגלות את העוצמה האמיתית שלנו, וכוחות
                    שקיימים בנו ולא הכרנו."
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="grid grid-cols-2 gap-4" data-id="element-2641" data-mp="3a6axzbpdpyjc">
              <div className="space-y-4 mt-8" data-id="element-2642" data-mp="1tsksq1mix484">
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5]" data-id="element-2643" data-mp="998mf66qn18c">
                  <video src="https://cdn.magicpatterns.com/uploads/o4YnUbrq6uHfsVbqHW7rax/IMG_5753.MOV" autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-2644" data-mp="e6qyh5g1xwkk" />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-square" data-id="element-2645" data-mp="nxln2ggrt896">
                  <img src="https://cdn.magicpatterns.com/uploads/sFwj9YB7rvdyCPXtDrtWRm/IMG_5724.jpg" alt="קבוצת נשים בבוקר נשי" className="w-full h-full object-cover" data-id="element-2646" data-mp="swgc405bzt1q" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-2647" data-mp="11wcytw85uhu5">
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-square" data-id="element-2648" data-mp="11xnz3m2ed7gi">
                  <img src="https://cdn.magicpatterns.com/uploads/eom8E644yCYiU5JAxE9kj9/IMG_5754.jpg" alt="אישה באמבטיית קרח" className="w-full h-full object-cover" data-id="element-2649" data-mp="16umpbtixwu9f" />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5]" data-id="element-2650" data-mp="38odr8wz5eofo">
                  <video src="https://cdn.magicpatterns.com/uploads/9XaBZJLpMsWrm3PxooNfNK/IMG_5728.MOV" autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-2651" data-mp="2u8uh1zmrfp7n" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Who */}
      <section className="py-24 px-4 bg-white" data-id="element-2652" data-mp="2pb2qpa1rkb5k">
        <div className="max-w-4xl mx-auto" data-id="element-2653" data-mp="2p7ony0p6tzxl">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-2654" data-mp="3d0nd62x4lqjl">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2655" data-mp="3hwcd47d259gf">
              הבוקר הזה הוא בשבילך אם...
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full" data-id="element-2656" data-mp="37zpt1ksk2mfg"></div>
          </motion.div>

          <motion.ul variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-4 max-w-2xl mx-auto" data-id="element-2657" data-mp="1rlzn08hvlo36">
            {['את מרגישה שמגיע לך בוקר אחד רק בשבילך', 'תמיד רצית לנסות סאפ אבל לא ידעת איפה להתחיל', 'את מתגעגעת למפגש נשי אמיתי', 'את אוהבת מים, שמש ושקט', 'בא לך להיות במרחב נטול שיפוטיות - מהסביבה וגם משלך כלפי עצמך', 'את רוצה להשיב לעצמך את הביטחון בגוף שלך, ולגלות שיש בך עוצמות ויכולות להתמודד'].map((item, idx) => <motion.li key={idx} variants={fadeInUp} className="flex items-start gap-4 bg-rose-50/50 p-5 rounded-2xl border border-rose-100" data-id="element-2658" data-mp="jmaiksd4iyxg" data-mpi={idx}>
                <div className="bg-rose-400 rounded-full p-1 shrink-0 mt-1" data-id="element-2659" data-mp="3s2vi7ijv8fnw">
                  <CheckCircle2 className="w-4 h-4 text-white" data-id="element-2660" data-mp="3n4lprfpizt5z" />
                </div>
                <span className="text-lg text-stone-700 font-medium leading-relaxed" data-id="element-2661" data-mp="4pt43s9n146o">
                  {item}
                </span>
              </motion.li>)}
          </motion.ul>
        </div>
      </section>

      {/* Details */}
      <section className="py-24 px-4 bg-gradient-to-b from-orange-50 to-rose-100" data-id="element-2662" data-mp="38d371a5a7kix">
        <div className="max-w-5xl mx-auto" data-id="element-2663" data-mp="34c9l2mbegfso">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2664" data-mp="34b86w8z37d6q">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2665" data-mp="1h1omtrltbbw3">
              לוז הבוקר
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full mb-6" data-id="element-2666" data-mp="1m09mnpx5jtk0"></div>
            <div className="inline-flex flex-wrap items-center justify-center gap-2 text-sm" data-id="element-2667" data-mp="oe2hoewxrz74">
              <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 text-stone-700 px-4 py-2 rounded-full shadow-sm" data-id="element-2668" data-mp="tc41j226s2jn">
                <MapPin className="w-4 h-4 text-rose-500" data-id="element-2669" data-mp="ydtckhpesg7q" />
                <span className="font-medium" data-id="element-2670" data-mp="13bv0ph1oz5q2">חוף מעגן, כנרת</span>
              </span>
              <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 text-stone-700 px-4 py-2 rounded-full shadow-sm" data-id="element-2671" data-mp="29rot9uvyox12">
                <Clock className="w-4 h-4 text-rose-500" data-id="element-2672" data-mp="32n5e1aafq0k" />
                <span className="font-medium" data-id="element-2673" data-mp="323nulojv3n2">09:00 - 13:00</span>
              </span>
            </div>
            <div className="mt-5 flex flex-col items-center justify-center gap-1.5 text-sm" data-id="element-2674" data-mp="3u9g5t2x9bl41">
              <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-full shadow-sm font-bold text-base" data-id="element-2675" data-mp="3pgn0odxvmarf">
                <Calendar className="w-4 h-4 text-emerald-500" data-id="element-2676" data-mp="3fka67duibsvm" />
                נותרו מקומות בודדים לבוקר נשי ב-4.9.2026
              </span>
              <span className="text-stone-500 font-medium" data-id="element-2677" data-mp="3bkjbiz1v0slt">
                תאריך באוקטובר יפורסם בקרוב
              </span>
            </div>
          </motion.div>

          <div className="max-w-3xl mx-auto mb-16" data-id="element-2678" data-mp="31lakadydps35">
            <div className="relative" data-id="element-2679" data-mp="14ryz8c1nvnjm">
              {/* Vertical timeline connector */}
              <div className="absolute right-6 md:right-8 top-3 bottom-3 w-px bg-gradient-to-b from-rose-300 via-rose-300 to-orange-300" data-id="element-2680" data-mp="14pml69z0xxbu"></div>

              <ul className="space-y-6" data-id="element-2681" data-mp="3tkm2qbsj90zo">
                {[{
                time: '09:00',
                title: 'מעגל פתיחה',
                desc: 'מפגש ראשון על החוף, היכרות וכוונה לבוקר'
              }, {
                time: '10:00',
                title: 'סאפ בכנרת',
                desc: 'הדרכה מקצועית למתחילות וגלישה רגועה על המים'
              }, {
                time: '11:30',
                title: 'בראנץ׳ נשי',
                desc: 'ארוחה מפנקת, יין טוב, שיחה אמיתית וסגירת מעגל'
              }, {
                time: '12:30',
                title: 'הדרכת נשימות ואמבטיית קרח',
                desc: 'חיבור למערכת הנשימה, ויסות סטרס וטבילה מעצימה במי קרח'
              }].map((item, idx) => {
                return <motion.li key={idx} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: idx * 0.1,
                  duration: 0.5
                }} className="relative flex items-start gap-4 md:gap-6" data-id="element-2682" data-mp="35vt5mc06cn3h" data-mpi={idx}>
                      {/* Time marker */}
                      <div className="relative z-10 shrink-0" data-id="element-2683" data-mp="2m9rgjhno2oqt">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex flex-col items-center justify-center shadow-md ring-4 ring-rose-100 border border-rose-200" data-id="element-2684" data-mp="2haemocydoheq">
                          <span className="text-rose-600 font-bold text-sm md:text-base leading-none" data-id="element-2685" data-mp="2v7te1n86yz9c">
                            {item.time}
                          </span>
                        </div>
                      </div>

                      {/* Content card */}
                      <div className="flex-1 bg-white rounded-2xl p-5 md:p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-2686" data-mp="301frhhm6j767">
                        <h3 className="text-lg md:text-xl font-bold text-stone-900 mb-2" data-id="element-2687" data-mp="39zc3scqus85w">
                          {item.title}
                        </h3>
                        <p className="text-stone-600 leading-relaxed" data-id="element-2688" data-mp="3h43gcq0f05bl">
                          {item.desc}
                        </p>
                      </div>
                    </motion.li>;
              })}
              </ul>
            </div>
          </div>

          {/* Price Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2689" data-mp="3vsr7mk389svq">
            <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-rose-300/30 border border-rose-200 bg-gradient-to-br from-rose-100 via-rose-50 to-orange-50" data-id="element-2690" data-mp="8ojo9ubhcsy4">
              <div className="relative z-10 p-8 md:p-12 text-center" data-id="element-2691" data-mp="ik4p3p4tbn6a">
                <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-rose-200 px-4 py-1.5 rounded-full mb-6 text-rose-600" data-id="element-2692" data-mp="2fdpw93jgfeq8">
                  <Users className="w-4 h-4" data-id="element-2693" data-mp="2csi7hzo0vner" />
                  <span className="text-sm font-medium" data-id="element-2694" data-mp="2cta51g16jv7o">מקומות מוגבלים</span>
                </div>

                <div className="text-rose-500 text-lg mb-3 font-medium" data-id="element-2695" data-mp="28106xcpwvoub">
                  השקעה בבוקר שלך
                </div>
                <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-2696" data-mp="230kviqp6wrez">
                  <span className="text-6xl md:text-7xl font-extrabold text-rose-700" data-id="element-2697" data-mp="1y55j836tz3ko">
                    ₪480
                  </span>
                </div>
                <p className="text-stone-600 text-sm mb-10" data-id="element-2698" data-mp="2qrqaz4r7xhor">
                  לאישה • כולל סאפ, הדרכה, אמבטיית קרח ובראנץ׳
                </p>

                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 mb-8 text-right border border-rose-100" data-id="element-2699" data-mp="2lrlsj96r2cvw">
                  <h4 className="font-bold mb-4 text-rose-600 text-center" data-id="element-2700" data-mp="2gw6g8loe4p1l">
                    מה כלול במחיר:
                  </h4>
                  <ul className="space-y-3" data-id="element-2701" data-mp="15ef6dj5a6d0a">
                    {['הדרכת סאפ מקצועית למתחילות', 'השכרת ציוד מלא (סאפ, משוט, חגורה)', 'הדרכת נשימות ואמבטיית קרח', 'בראנץ׳ עשיר בסוף הבוקר', 'קפה ופינוקים לאורך הבוקר'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-2702" data-mp="202f8w942pytu" data-mpi={i}>
                        <CheckCircle2 className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-2703" data-mp="1m2nqlw6ixwa1" />
                        <span className="text-stone-700 font-medium" data-id="element-2704" data-mp="1q3zjcakp2xmg">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <motion.button type="button" onClick={openForm} animate={{
                scale: [1, 1.03, 1]
              }} transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="group flex items-center justify-center gap-2.5 w-full bg-rose-500 hover:bg-rose-600 text-white text-xl md:text-2xl font-extrabold py-5 rounded-2xl transition-colors shadow-2xl shadow-rose-400/60 ring-4 ring-rose-200/70 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300" data-id="element-2705" data-mp="2isn84g4nk54r">
                  <Sparkles className="w-6 h-6 shrink-0" data-id="element-2706" data-mp="q2kzj3klq0nr" />
                  <span data-id="element-2707" data-mp="l5m6ypgl61ro">להרשמה לבוקר הנשי</span>
                  <span className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-2708" data-mp="l4swpeif4u4s">
                    ←
                  </span>
                </motion.button>
                <p className="text-stone-500 text-sm mt-4 font-medium" data-id="element-2709" data-mp="uw72g40yqq6d">
                  השארת פרטים והמשך לתשלום מאובטח
                </p>
              </div>
            </div>
          </motion.div>

          {/* Questions / WhatsApp CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto mt-10" data-id="element-2710" data-mp="28kzmxzk7pkk">
            <div className="bg-white border border-rose-100 rounded-3xl p-8 text-center shadow-sm" data-id="element-2711" data-mp="1si4ogbejxdn2">
              <div className="inline-flex items-center gap-2 mb-3 text-rose-500" data-id="element-2712" data-mp="1nk2wt23285zz">
                <Heart className="w-5 h-5" data-id="element-2713" data-mp="3eci8530fbw29" />
                <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2714" data-mp="3ebgtypo42tgb">
                  אני כאן בשבילך
                </span>
                <Heart className="w-5 h-5" data-id="element-2715" data-mp="39bugcf4dfb6z" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3 leading-tight" data-id="element-2716" data-mp="3n4k52rbaau7s">
                מתלבטת? יש לך עוד שאלות?
              </h3>
              <p className="text-stone-600 text-lg mb-7 leading-relaxed" data-id="element-2717" data-mp="2vdmhg1nrxzt6">
                תכתבי לי בוואטסאפ ואשמח לדבר ולענות על כל שאלה 💌
              </p>
              <a href="https://wa.me/972548800260?text=היי%20עדי,%20יש%20לי%20כמה%20שאלות%20על%20הבוקר%20הנשי%20-%20הכוח%20שבמים" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-4 px-8 rounded-full transition-all shadow-lg shadow-emerald-300/40 hover:scale-105" data-id="element-2718" data-mp="2qgnklbcq7era">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2719" data-mp="eo8mv0biucbf">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2720" data-mp="n4zeo0xo8ol2" />
                </svg>
                תכתבי לי בוואטסאפ
              </a>
            </div>
          </motion.div>

          {/* Cancellation Policy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto mt-10" data-id="element-2721" data-mp="1pi0rxu9unspy">
            <div className="bg-white border border-rose-100 rounded-3xl p-8 shadow-sm text-right" data-id="element-2722" data-mp="1fqxtl08b1rud">
              <div className="flex items-center justify-center gap-2 mb-5 text-rose-500" data-id="element-2723" data-mp="1arbf6ngqq5jz">
                <Info className="w-5 h-5" data-id="element-2724" data-mp="15si2gx9a2ikv" />
                <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2725" data-mp="11t97jp2i4enq">
                  מדיניות ביטולים
                </span>
              </div>
              <ul className="space-y-3" data-id="element-2726" data-mp="wvzjacqum91h">
                {[{
                when: 'ביטול עד 3 ימים לפני המועד',
                detail: 'חיוב של 25% מעלות הבוקר'
              }, {
                when: 'ביטול יום לפני המועד',
                detail: 'חיוב של 50% מעלות הבוקר'
              }, {
                when: 'ביטול ב-24 השעות שלפני האירוע',
                detail: 'ללא זיכוי'
              }].map((item, i) => <li key={i} className="flex items-start gap-3 bg-rose-50/60 border border-rose-100 rounded-2xl p-4" data-id="element-2727" data-mp="3menbhtd5g9o1" data-mpi={i}>
                    <Clock className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" data-id="element-2728" data-mp="2ts2i6nd45nhu" />
                    <div data-id="element-2729" data-mp="2tpzo9s8uba7u">
                      <span className="block font-bold text-stone-800" data-id="element-2730" data-mp="2yqx0zpb2zc1y">
                        {item.when}
                      </span>
                      <span className="block text-stone-600 text-sm mt-0.5" data-id="element-2731" data-mp="33jq4yayq6gcz">
                        {item.detail}
                      </span>
                    </div>
                  </li>)}
              </ul>
              <p className="text-stone-500 text-xs text-center mt-5 leading-relaxed" data-id="element-2732" data-mp="2b01ftb14rf3o">
                בהרשמה ובתשלום את מאשרת את מדיניות הביטולים המפורטת מעלה.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-12 text-center border-t border-stone-800" data-id="element-2733" data-mp="qch3nl93xo5z">
        <div className="max-w-4xl mx-auto px-4" data-id="element-2734" data-mp="ljnzayhjwhuf">
          <h2 className="text-2xl font-bold text-white mb-3" data-id="element-2735" data-mp="liw1dh0he80z">
            בוקר נשי - הכוח שבמים
          </h2>
          <p className="mb-6 text-rose-200/80" data-id="element-2736" data-mp="vaily3ypljge">
            ארבע שעות שיחזירו אותך אל עצמך.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm" data-id="element-2737" data-mp="19a1j61pzfadx">
            <span className="flex items-center gap-2" data-id="element-2738" data-mp="197yv3n8e3rbu">
              <Calendar size={16} data-id="element-2739" data-mp="159raghat25vd" /> 04.09.2026
            </span>
            <span className="flex items-center gap-2" data-id="element-2740" data-mp="3b1ek6fotwy4p">
              <Clock size={16} data-id="element-2741" data-mp="3b1y5859s02my" /> 09:00 - 13:00
            </span>
            <span className="flex items-center gap-2" data-id="element-2742" data-mp="370ulv8sirogz">
              <MapPin size={16} data-id="element-2743" data-mp="372fl3bps3vl6" /> חוף מעגן
            </span>
          </div>
          <p className="text-sm opacity-60" data-id="element-2744" data-mp="2wwxsm9fmxjxj">© 2026 כל הזכויות שמורות.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/972548800260?text=היי,%20אני%20רוצה%20להירשם%20לבוקר%20הנשי%20-%20הכוח%20שבמים" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 left-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 flex items-center justify-center group" aria-label="Contact on WhatsApp" data-id="element-2745" data-mp="2n6kxxoe3wado">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2746" data-mp="2i55g05786jzi">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2747" data-mp="296jz0ehejd0u" />
        </svg>
      </a>

      {/* Registration Modal */}
      <AnimatePresence data-id="element-2748" data-mp="17gtvh1mlhvl0">
        {isFormOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeForm} className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(60, 20, 40, 0.55)',
        backdropFilter: 'blur(6px)'
      }} data-id="element-2749" data-mp="12o0n83rx4546">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} dir="rtl" className="relative bg-gradient-to-br from-rose-50 via-white to-orange-50 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-rose-200" data-id="element-2750" data-mp="1jw1pc7db6s57">
              {/* Close */}
              <button type="button" onClick={closeForm} disabled={isSubmitting} className="absolute top-4 left-4 text-stone-400 hover:text-stone-700 transition-colors p-2 rounded-full hover:bg-white/70 z-10 disabled:opacity-50" aria-label="סגירה" data-id="element-2751" data-mp="2co4t7fqxx8ox">
                <X className="w-5 h-5" data-id="element-2752" data-mp="2xxryike9td0c" />
              </button>

              <div className="p-7 md:p-9" data-id="element-2753" data-mp="2xzl5y1wpk19e">
                {/* Header */}
                <div className="text-center mb-6" data-id="element-2754" data-mp="3bwhws3cttiqa">
                  <div className="inline-flex items-center gap-2 mb-3 text-rose-500" data-id="element-2755" data-mp="36xf0p8etqmug">
                    <Heart className="w-5 h-5" data-id="element-2756" data-mp="2j8ccfhpopvqz" />
                    <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2757" data-mp="2j942ye4fd3ae">
                      הרשמה
                    </span>
                    <Heart className="w-5 h-5" data-id="element-2758" data-mp="2sy5ki8qmbsqh" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2" data-id="element-2759" data-mp="2szgni6c8owgj">
                    בוקר נשי - הכוח שבמים
                  </h3>
                  <p className="text-stone-600 text-sm" data-id="element-2760" data-mp="20hbaqlx6jm3q">
                    השאירי פרטים ונמשיך לתשלום מאובטח
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2761" data-mp="1vgno0cfzhrfb">
                  {/* Full name */}
                  <div data-id="element-2762" data-mp="aymnfmd7f69a">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2763" data-mp="fr5z2dsbzrbg">
                      שם מלא <span className="text-rose-500" data-id="element-2764" data-mp="20aw1i4ik4fs">*</span>
                    </label>
                    <div className="relative" data-id="element-2765" data-mp="61me92l25ndy">
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2766" data-mp="62fnqbbeiqzs" />
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required disabled={isSubmitting} placeholder="השם המלא שלך" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all" data-id="element-2767" data-mp="ts0bd7ii2cfe" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-2768" data-mp="3agvx8cqwlt8c">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2769" data-mp="3ahfdzq4tibks">
                      כתובת מייל <span className="text-rose-500" data-id="element-2770" data-mp="28icm0zvjbmtd">*</span>
                    </label>
                    <div className="relative" data-id="element-2771" data-mp="2dg4b7m4soutv">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2772" data-mp="2ih1s7ve2jits" />
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} required disabled={isSubmitting} placeholder="name@example.com" dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2773" data-mp="2mina5v5gmnau" />
                    </div>
                  </div>

                  {/* Phone */}
                  <div data-id="element-2774" data-mp="172518fybqjjs">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2775" data-mp="170k3yikmkmi8">
                      מספר טלפון <span className="text-rose-500" data-id="element-2776" data-mp="1b1w0l81x27zx">*</span>
                    </label>
                    <div className="relative" data-id="element-2777" data-mp="13imq1bhoc39a">
                      <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2778" data-mp="13hbn1dw1yzj8" />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required disabled={isSubmitting} placeholder="050-1234567" dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2779" data-mp="uircx14k5y6m" />
                    </div>
                  </div>

                  {/* Preferred event date */}
                  <div data-id="element-2780" data-mp="6touvs60i3bl">
                    <label className="block text-sm font-semibold text-stone-700 mb-2" data-id="element-2781" data-mp="6u83ujdophdh">
                      לאיזה תאריך תרצי להצטרף?{' '}
                      <span className="text-rose-500" data-id="element-2782" data-mp="1tkfk5gubemy">*</span>
                    </label>
                    <div className="grid grid-cols-1 gap-3" data-id="element-2783" data-mp="3t155q1aq6d8s">
                      {EVENT_DATES.map(opt => {
                    const active = formData.selectedDate === opt.value;
                    return <button key={opt.value} type="button" disabled={isSubmitting} onClick={() => setFormData(prev => ({
                      ...prev,
                      selectedDate: opt.value
                    }))} className={`flex items-center justify-center gap-2 py-3 px-2 rounded-xl font-bold text-sm border-2 transition-all ${active ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-300/50' : 'bg-white text-stone-700 border-rose-200 hover:border-rose-400'}`} data-id="element-2784" data-mp="3ocvqbih3292e">
                            <Calendar className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-rose-400'}`} data-id="element-2785" data-mp="3scxubyaajeku" />
                            {opt.label}
                          </button>;
                  })}
                    </div>
                    <p className="mt-2 text-xs text-stone-500" data-id="element-2786" data-mp="ouw64ekinr8i">
                      תאריך באוקטובר יפורסם בקרוב
                    </p>
                  </div>

                  {/* Date of birth */}
                  <div data-id="element-2787" data-mp="2ec1epryhi43a">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2788" data-mp="29fazt7yf1ejg">
                      תאריך לידה <span className="text-rose-500" data-id="element-2789" data-mp="2o3nywdssvdi6">*</span>
                    </label>
                    <div className="relative" data-id="element-2790" data-mp="2j4uiaci7v6ds">
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2791" data-mp="2j65m2cbnwe4w" />
                      <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleFormChange} required disabled={isSubmitting} dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2792" data-mp="1vhcqigyyc8at" />
                    </div>
                  </div>

                  {/* No medical limitations checkbox */}
                  <div data-id="element-2793" data-mp="3bt8m58vzkwx9">
                    <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all ${formData.noMedicalLimitations ? 'bg-rose-50 border-rose-400' : 'bg-white border-rose-200 hover:border-rose-300'} ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`} data-id="element-2794" data-mp="ayajh7mtf4zq">
                      <input type="checkbox" name="noMedicalLimitations" checked={formData.noMedicalLimitations} onChange={e => setFormData(prev => ({
                    ...prev,
                    noMedicalLimitations: e.target.checked
                  }))} disabled={isSubmitting} required className="sr-only" data-id="element-2795" data-mp="k1bpl8bokw3e" />
                      <div className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${formData.noMedicalLimitations ? 'bg-rose-500 border-rose-500' : 'bg-white border-rose-300'}`} data-id="element-2796" data-mp="12pqenq2fu01k">
                        {formData.noMedicalLimitations && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} data-id="element-2797" data-mp="1cph8rqn36d14" />}
                      </div>
                      <div className="flex-1" data-id="element-2798" data-mp="1hkd3iyjpjehx">
                        <span className="block text-sm font-semibold text-stone-800" data-id="element-2799" data-mp="325tbe59x64et">
                          אני מאשרת שאין לי מגבלות רפואיות{' '}
                          <span className="text-rose-500" data-id="element-2800" data-mp="2x81lfgsu4sd9">*</span>
                        </span>
                        <span className="block text-xs text-stone-500 mt-0.5" data-id="element-2801" data-mp="2s8op85g2q8y0">
                          המאפשרות לי להשתתף בפעילות סאפ במים
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* SUP experience yes/no */}
                  <div data-id="element-2802" data-mp="3fy9fzaigyahu">
                    <label className="block text-sm font-semibold text-stone-700 mb-2" data-id="element-2803" data-mp="3b56od7x95b4a">
                      האם יש לך ניסיון על סאפ?{' '}
                      <span className="text-rose-500" data-id="element-2804" data-mp="365lo8u6ciwpz">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2805" data-mp="7heo0q37maij">
                      {[{
                    value: 'yes',
                    label: 'כן'
                  }, {
                    value: 'no',
                    label: 'לא'
                  }].map(opt => {
                    const active = formData.hasExperience === opt.value;
                    return <button key={opt.value} type="button" disabled={isSubmitting} onClick={() => setFormData(prev => ({
                      ...prev,
                      hasExperience: opt.value
                    }))} className={`py-3 rounded-xl font-bold text-sm border-2 transition-all ${active ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-300/50' : 'bg-white text-stone-700 border-rose-200 hover:border-rose-400'}`} data-id="element-2806" data-mp="1n7p2kcxowjhb">
                            {opt.label}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Notes / sensitivities */}
                  <div data-id="element-2807" data-mp="2ynvucpbo951n">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2808" data-mp="2yndt1ea5k0mv">
                      הערות / רגישויות שחשוב שנכיר
                    </label>
                    <textarea name="notes" value={formData.notes} onChange={handleFormChange} disabled={isSubmitting} rows={3} placeholder="אלרגיות, רגישויות תזונה, כל דבר שחשוב לי לדעת..." className="w-full bg-white border border-rose-200 rounded-xl py-3 px-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all resize-none" data-id="element-2809" data-mp="rxsftiwi12yb" />
                  </div>

                  {/* Submit */}
                  <button type="submit" disabled={isSubmitting} className="w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-70 disabled:cursor-not-allowed text-white text-lg font-bold py-4 rounded-2xl transition-all shadow-lg shadow-rose-300/60 hover:scale-[1.01] mt-2" data-id="element-2810" data-mp="15q0b1684wluw">
                    {isSubmitting ? 'פותחת את חלון התשלום…' : 'המשך לתשלום ←'}
                  </button>

                  <p className="text-stone-500 text-xs text-center pt-1" data-id="element-2811" data-mp="3mgqfhj4c7ior">
                    התשלום מאובטח ומבוצע דרך Tranzila
                  </p>

                  {/* Cancellation policy note */}
                  <div className="bg-rose-50/70 border border-rose-100 rounded-xl p-3 text-right" data-id="element-2812" data-mp="2vkj6a6bhvjju">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-500" data-id="element-2813" data-mp="30j3ut8hdwesg">
                      <Info className="w-3.5 h-3.5" data-id="element-2814" data-mp="35cylwwzbjw1k" />
                      <span className="text-[11px] font-bold" data-id="element-2815" data-mp="3abjcs5sol3dc">
                        מדיניות ביטולים
                      </span>
                    </div>
                    <p className="text-stone-500 text-[11px] leading-relaxed" data-id="element-2816" data-mp="2cpueymgo17n5">
                      ביטול עד 3 ימים לפני — חיוב 25% · ביטול יום לפני — חיוב
                      50% · ביטול ב-24 השעות שלפני האירוע — ללא זיכוי.
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Payment Modal (Tranzila iframe) */}
      <AnimatePresence data-id="element-2817" data-mp="5wiidksblr9z">
        {isPaymentOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(60, 20, 40, 0.65)',
        backdropFilter: 'blur(6px)'
      }} onClick={closePayment} data-id="element-2818" data-mp="5vgvmj1xzgcf">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} dir="rtl" className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden border border-rose-200 flex flex-col" data-id="element-2819" data-mp="1cnpc9z7zjcs5">
              {/* Header */}
              <div className="bg-gradient-to-l from-rose-50 to-orange-50 px-6 py-4 border-b border-rose-100 flex items-center justify-between shrink-0" data-id="element-2820" data-mp="2szuxweq2e8ka">
                <div className="flex items-center gap-2 text-stone-700" data-id="element-2821" data-mp="3hdp44ccsmo4c">
                  {paymentSuccess ? <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2822" data-mp="3cfnjhmzpygqr" />
                      <span className="text-sm font-semibold" data-id="element-2823" data-mp="37mugb3jwfggs">
                        ההרשמה הושלמה
                      </span>
                    </> : <>
                      <Lock className="w-4 h-4 text-rose-500" data-id="element-2824" data-mp="32mowg9jvey99" />
                      <span className="text-sm font-semibold" data-id="element-2825" data-mp="2xq7vgerkoper">
                        תשלום מאובטח באמצעות טרנזילה
                      </span>
                    </>}
                </div>
                <div className="flex items-center gap-4" data-id="element-2826" data-mp="2opt9reap58aq">
                  {!paymentSuccess && <div className="text-sm font-bold text-rose-600 hidden sm:block" data-id="element-2827" data-mp="2jq6vr2n1no0v">
                      בוקר נשי • ₪{PRICE}
                    </div>}
                  <button type="button" onClick={closePayment} className="text-stone-400 hover:text-stone-700 transition-colors p-1.5 rounded-full hover:bg-white/70" aria-label="סגירת חלון התשלום" data-id="element-2828" data-mp="mwlu23r68oob">
                    <X className="w-5 h-5" data-id="element-2829" data-mp="phjleyz8b6k2" />
                  </button>
                </div>
              </div>

              {/* Body — either iframe or success state */}
              {paymentSuccess ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex-1 bg-gradient-to-br from-rose-50 via-white to-orange-50 px-8 py-12 text-center flex flex-col items-center justify-center" data-id="element-2830" data-mp="z84oaylxqx8s">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 14,
              stiffness: 200,
              delay: 0.1
            }} className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6 ring-8 ring-emerald-50" data-id="element-2831" data-mp="bik46ce1ptxx">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" data-id="element-2832" data-mp="1wu242o8xkoh1" />
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3" data-id="element-2833" data-mp="3sx85vrtkvshv">
                    ההרשמה הצליחה! 🌸
                  </h3>
                  <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-md mb-2" data-id="element-2834" data-mp="5ttn1la74lty">
                    קיבלנו את ההרשמה ואת התשלום שלך לבוקר הנשי.
                  </p>
                  <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-md mb-8" data-id="element-2835" data-mp="1qdv3292n9nl">
                    אשלח אלייך פרטים נוספים בוואטסאפ בקרוב 💌
                  </p>

                  <div className="bg-white border border-rose-100 rounded-2xl p-5 max-w-sm w-full mb-6 text-right space-y-2" data-id="element-2836" data-mp="35arwkltnsj6n">
                    <div className="flex items-start gap-2 text-stone-700" data-id="element-2837" data-mp="3e9n6o8ztqdf8">
                      <Calendar className="w-4 h-4 text-rose-500 mt-1 shrink-0" data-id="element-2838" data-mp="3e7itf0g1c22a" />
                      <span className="font-medium" data-id="element-2839" data-mp="2qihr7uxspn5g">
                        שישי, 04.09.2026
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-700" data-id="element-2840" data-mp="2cesidyx3oc75">
                      <Clock className="w-4 h-4 text-rose-500" data-id="element-2841" data-mp="2cco36ktr3qrk" />
                      <span className="font-medium" data-id="element-2842" data-mp="2h6scvbueicwc">09:00 - 13:00</span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-700" data-id="element-2843" data-mp="22hnx01b2wkre">
                      <MapPin className="w-4 h-4 text-rose-500" data-id="element-2844" data-mp="27emjpysexpfk" />
                      <span className="font-medium" data-id="element-2845" data-mp="2v50gg2ly0cgh">חוף מעגן, כנרת</span>
                    </div>
                  </div>

                  <button type="button" onClick={closePayment} className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-8 py-3 rounded-full transition-all shadow-lg shadow-rose-300/50 hover:scale-[1.02]" data-id="element-2846" data-mp="304mrq6i7nimn">
                    סיום
                  </button>
                </motion.div> : <>
                  <div className="flex-1 bg-rose-50/50 overflow-hidden" data-id="element-2847" data-mp="19r5qsrf2639r">
                    <iframe ref={paymentIframeRef} name="tranzila" width="100%" height="600" frameBorder="0" allow="payment" className="w-full h-[600px] max-h-[78vh] bg-white" title="חלון תשלום מאובטח" data-id="element-2848" data-mp="1eqquff56azst"></iframe>
                  </div>
                  {/* Cancellation policy strip inside the payment window */}
                  <div className="shrink-0 bg-rose-50 border-t border-rose-100 px-5 py-3 text-right" data-id="element-2849" data-mp="1l7twqepuu20b">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-500" data-id="element-2850" data-mp="1g9ip8an183l9">
                      <Info className="w-3.5 h-3.5 shrink-0" data-id="element-2851" data-mp="2dwr25h8yw4bn" />
                      <span className="text-[11px] font-bold" data-id="element-2852" data-mp="2dvzk798am53u">
                        מדיניות ביטולים
                      </span>
                    </div>
                    <p className="text-stone-500 text-[11px] leading-relaxed" data-id="element-2853" data-mp="23y1u0g6vhy8l">
                      ביטול עד 3 ימים לפני — חיוב 25% · ביטול יום לפני — חיוב
                      50% · ביטול ב-24 השעות שלפני האירוע — ללא זיכוי.
                    </p>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}